/**
 * Spec 217 PR4 §4.3 — Cloud backup status callout.
 *
 * The web client owns the team key but NOT the backup key (per the
 * "Two keys from one phrase" rule in `mobile/CLAUDE.md`: the backup
 * key is derived from the 12-word phrase and only ever lives on
 * mobile). Consequently:
 *
 *   - Web cannot decrypt or restore a backup.
 *   - Web cannot trigger a fresh backup (it has no key to encrypt with).
 *   - Web CAN show a callout pointing the admin to the mobile flow.
 *
 * This card is admin-only (`creator` / `admin`). Workers don't manage
 * backups so the section never renders for them.
 *
 * The card is intentionally educational + read-only. No fetch, no
 * privileged endpoint call from web — keeps the attack surface
 * smaller while we wait on a `/backups/by-company` endpoint that
 * the relay does NOT currently expose (existing endpoints are
 * keyed by `x-key-hash` which is the backup-key hash, not a
 * companyToken-readable surface).
 */
import { CloudUpload, Smartphone } from 'lucide-react';

import './BackupStatusCard.css';

export function BackupStatusCard({ testId = 'settings-backup-card' }: { testId?: string }) {
  return (
    <section className="backup-card" data-testid={testId} aria-label="Copia de seguridad en la nube">
      <header className="backup-card-header">
        <CloudUpload size={20} strokeWidth={2} aria-hidden="true" />
        <h2 className="backup-card-title">Copia de seguridad en la nube</h2>
      </header>

      <p className="backup-card-body" data-testid={`${testId}-body`}>
        Las copias de seguridad se cifran con la clave derivada de tus 12
        palabras de recuperación, que sólo vive en el móvil del propietario
        o un administrador. Por privacidad, esta sesión web no puede crear
        ni restaurar copias.
      </p>

      <div className="backup-card-callout">
        <Smartphone size={20} strokeWidth={2} aria-hidden="true" />
        <span>
          Para gestionar copias, abre Cuadrilla en el móvil del{' '}
          <strong>creador</strong> o un <strong>administrador</strong> y entra
          en <em>Ajustes → Copia de seguridad</em>.
        </span>
      </div>

      <ul className="backup-card-bullets">
        <li>Las copias suben automáticamente cada día.</li>
        <li>El servidor guarda los últimos 30 días (TTL).</li>
        <li>El servidor nunca puede leerlas — sólo las almacena cifradas.</li>
      </ul>
    </section>
  );
}
